import { useState, forwardRef } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from '@leadsnavi/ui-kit';
import cn from 'classnames';

interface PasswordInputProps {
  /** 字段标签 */
  label: string;
  /** 输入框值 */
  value: string;
  /** 值变化回调 */
  onChange: (value: string) => void;
  /** 占位符 */
  placeholder?: string;
  /** 错误信息 */
  error?: string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 右侧标签内容（如"Forgot your password?"） */
  rightLabel?: React.ReactNode;
  /** 自定义类名 */
  className?: string;
  /** 失焦回调 */
  onBlur?: () => void;
}

/**
 * 密码输入框组件（带显示/隐藏切换）
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ label, value, onChange, placeholder, error, disabled = false, rightLabel, className, onBlur }, ref) => {
    const [showPassword, setShowPassword] = useState(false);

    return (
      <div className={cn('flex flex-col gap-[6px]', className)}>
        {/* Label 行 */}
        <div className="flex items-center">
          <label className="flex-1 text-[14px] font-medium leading-[1.5] tracking-[0.07px] text-[#171717]">
            {label}
          </label>
          {rightLabel && <div className="flex-1 text-right">{rightLabel}</div>}
        </div>

        {/* 输入框 */}
        <div className="relative">
          <Input
            ref={ref}
            type={showPassword ? 'text' : 'password'}
            value={value}
            onChange={e => onChange(e.target.value)}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(
              'h-[40px] w-full rounded-[8px] border bg-white px-[16px] py-[9.5px] pr-[44px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]',
              'text-[14px] font-normal leading-[1.5] tracking-[0.07px] text-[#171717]',
              'placeholder:text-[#a3a3a3]',
              'focus:outline-none',
              error
                ? 'border-[#ef4444] focus:shadow-[0_0_0_3px_#fca5a5]'
                : 'border-[#d4d4d4] focus:border-[#a3a3a3] focus:shadow-[0_0_0_3px_#d4d4d4]'
            )}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-[16px] top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#525252]"
          >
            {showPassword ? <EyeOff className="h-[20px] w-[20px]" /> : <Eye className="h-[20px] w-[20px]" />}
          </button>
        </div>

        {/* 错误信息 */}
        {error && <p className="text-[12px] font-normal leading-[1.5] tracking-[0.18px] text-[#ef4444]">{error}</p>}
      </div>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
